import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import CodeIcon from '@mui/icons-material/Code';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { storage } from '../../firebase';
import { uploadTicketAttachment } from '../../lib/ticketAttachments';

/**
 * TipTap editor for ticket descriptions and comments (dark-theme styled).
 * @param {{
 *   value: string,
 *   onChange: (html: string) => void,
 *   disabled?: boolean,
 *   minHeight?: number,
 *   placeholder?: string,
 *   ticketId?: string | null,
 * }} props
 */
export default function TicketRichTextEditor({
  value,
  onChange,
  disabled = false,
  minHeight = 120,
  placeholder = 'Write something…',
  ticketId = null,
}) {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        horizontalRule: false,
        link: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || '';
    if (current !== next) {
      editor.commands.setContent(next, false);
    }
  }, [editor, value]);

  const runCmd = useCallback((fn) => {
    if (!editor || disabled) return;
    fn(editor);
  }, [editor, disabled]);

  const handleAttachClick = () => {
    if (!ticketId) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editor || !ticketId) return;

    setUploading(true);
    setUploadError('');
    try {
      const { url, name } = await uploadTicketAttachment(storage, ticketId, file);
      const label = name || 'attachment';
      editor
        .chain()
        .focus()
        .insertContent(`<p><a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a></p>`)
        .run();
    } catch (err) {
      console.error('TicketRichTextEditor upload:', err);
      setUploadError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const toolbarBtnSx = (active) => ({
    color: active ? 'primary.main' : 'text.secondary',
    bgcolor: active ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
    borderRadius: 1,
  });

  if (!editor) {
    return (
      <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        opacity: disabled ? 0.7 : 1,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.25}
        sx={{
          px: 0.5,
          py: 0.25,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: (t) => alpha(t.palette.background.default, 0.6),
          flexWrap: 'wrap',
        }}
      >
        <Tooltip title="Bold">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => runCmd((ed) => ed.chain().focus().toggleBold().run())}
              sx={toolbarBtnSx(editor.isActive('bold'))}
              aria-label="Bold"
            >
              <FormatBoldIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Italic">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => runCmd((ed) => ed.chain().focus().toggleItalic().run())}
              sx={toolbarBtnSx(editor.isActive('italic'))}
              aria-label="Italic"
            >
              <FormatItalicIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Blockquote">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => runCmd((ed) => ed.chain().focus().toggleBlockquote().run())}
              sx={toolbarBtnSx(editor.isActive('blockquote'))}
              aria-label="Blockquote"
            >
              <FormatQuoteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Code block">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => runCmd((ed) => ed.chain().focus().toggleCodeBlock().run())}
              sx={toolbarBtnSx(editor.isActive('codeBlock'))}
              aria-label="Code block"
            >
              <CodeIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={ticketId ? 'Attach file' : 'Save the ticket first to attach files'}>
          <span>
            <IconButton
              size="small"
              disabled={disabled || uploading || !ticketId}
              onClick={handleAttachClick}
              sx={toolbarBtnSx(false)}
              aria-label="Attach file"
            >
              {uploading ? <CircularProgress size={16} /> : <AttachFileIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={handleFileChange}
        />
      </Stack>

      <EditorContent
        editor={editor}
        className="ticket-rich-text-editor"
      />

      {uploadError && (
        <Typography variant="caption" color="error" sx={{ px: 1.5, py: 0.5, display: 'block' }}>
          {uploadError}
        </Typography>
      )}

      <style>{`
        .ticket-rich-text-editor .ProseMirror {
          min-height: ${minHeight}px;
          padding: 12px 14px;
          outline: none;
          color: ${theme.palette.text.primary};
          font-family: ${theme.typography.body2.fontFamily};
          font-size: ${theme.typography.body2.fontSize};
          line-height: 1.6;
        }
        .ticket-rich-text-editor .ProseMirror p.is-editor-empty:first-child::before {
          color: ${theme.palette.text.disabled};
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .ticket-rich-text-editor .ProseMirror blockquote {
          border-left: 3px solid ${theme.palette.divider};
          margin: 0.5em 0;
          padding-left: 1em;
          color: ${theme.palette.text.secondary};
        }
        .ticket-rich-text-editor .ProseMirror pre {
          background: ${alpha(theme.palette.action.selected, 0.9)};
          border-radius: ${theme.shape.borderRadius}px;
          padding: 0.75em 1em;
          overflow-x: auto;
          font-family: ui-monospace, monospace;
          font-size: 0.85em;
        }
        .ticket-rich-text-editor .ProseMirror code {
          background: ${theme.palette.action.hover};
          border-radius: 4px;
          padding: 0.1em 0.35em;
          font-family: ui-monospace, monospace;
          font-size: 0.85em;
        }
        .ticket-rich-text-editor .ProseMirror pre code {
          background: transparent;
          padding: 0;
        }
        .ticket-rich-text-editor .ProseMirror a {
          color: ${theme.palette.primary.main};
          word-break: break-all;
        }
      `}</style>
    </Box>
  );
}
